<div class="integration-page">
  <div class="page-header">
    <h1 class="page-title">{{ 'app.patrol.integrationRequest.title' | translate }}</h1>
    <p class="page-subtitle">
      {{ 'app.patrol.integrationRequest.subtitle' | translate }}
    </p>
  </div>

  @if (pageError()) {
    <app-info-banner
      class="integration-page__banner"
      variant="warning"
      [title]="'app.patrol.integrationRequest.actionErrorTitle'"
      [message]="pageError()!"
      [ariaLabel]="'app.patrol.integrationRequest.actionErrorAria'"
      [translateTitle]="true"
      [translateMessage]="false"
      [translateAriaLabel]="true"
    />
  }

  @if (showLoadError()) {
    <app-info-banner
      class="integration-page__banner"
      variant="warning"
      [title]="'app.patrol.integrationRequest.loadErrorTitle'"
      [message]="'app.patrol.integrationRequest.loadErrorHint'"
      [ariaLabel]="'app.patrol.integrationRequest.loadErrorAria'"
      [translateTitle]="true"
      [translateMessage]="true"
      [translateAriaLabel]="true"
      iconUrl="/public/svgs/refresh.svg"
    />
  } @else if (isLoading()) {
    <section class="requests-section requests-section--loading" aria-busy="true">
      <p class="requests-section__loading">
        {{ 'app.patrol.integrationRequest.loading' | translate }}
      </p>
    </section>
  } @else {
    <section
      #requestsSection
      class="requests-section"
      [attr.aria-labelledby]="showForm() ? 'integration-form-heading' : 'integration-requests-heading'"
    >
      @if (showForm()) {
        <header class="requests-section__header requests-section__header--form">
          <button type="button" class="requests-section__back" (click)="cancelForm()">
            <img src="/public/svgs/arrow-left-g.svg" alt="" width="16" height="16" aria-hidden="true" />
            {{ 'app.patrol.integrationRequest.backToRequests' | translate }}
          </button>
          <h2 id="integration-form-heading" class="requests-section__form-title">
            @if (editingRequestId()) {
              {{ 'app.patrol.integrationRequest.editTitle' | translate }}
            } @else {
              {{ 'app.patrol.integrationRequest.createTitle' | translate }}
            }
          </h2>
        </header>

        <form class="integration-form" (ngSubmit)="saveForm()">
          <label class="field">
            <span>{{ 'app.patrol.integrationRequest.fields.clientId' | translate }}</span>
            <input
              type="text"
              name="clientId"
              [(ngModel)]="formState.requestedClientId"
              required
              pattern="[a-z0-9][a-z0-9._-]{2,63}"
              autocomplete="off"
            />
            <small>{{ 'app.patrol.integrationRequest.fields.clientIdHint' | translate }}</small>
          </label>

          <label class="field">
            <span>{{ 'app.patrol.integrationRequest.fields.appName' | translate }}</span>
            <input type="text" name="appName" [(ngModel)]="formState.appName" required />
          </label>

          <label class="field">
            <span>{{ 'app.patrol.integrationRequest.fields.providerType' | translate }}</span>
            <select name="providerType" [(ngModel)]="formState.providerType">
              @for (type of providerTypes; track type) {
                <option [value]="type">
                  {{ 'app.patrol.integrationRequest.provider.' + type | translate }}
                </option>
              }
            </select>
          </label>

          <label class="field">
            <span>{{ 'app.patrol.integrationRequest.fields.environment' | translate }}</span>
            <select name="environment" [(ngModel)]="formState.environment">
              @for (env of environments; track env) {
                <option [value]="env">
                  {{ 'app.patrol.integrationRequest.environment.' + env | translate }}
                </option>
              }
            </select>
          </label>

          <label class="field">
            <span>{{ 'app.patrol.integrationRequest.fields.redirectUris' | translate }}</span>
            <div class="uri-editor">
              @for (uri of formState.redirectUris; track $index; let i = $index) {
                <div class="uri-editor__row">
                  <input
                    type="url"
                    [name]="'redirectUri' + i"
                    [ngModel]="uri"
                    (ngModelChange)="updateRedirectUri(i, $event)"
                    required
                    autocomplete="off"
                  />
                  <button
                    type="button"
                    class="uri-editor__copy"
                    [disabled]="!uri.trim()"
                    [attr.aria-label]="
                      (isCopied('form-uri:' + i)
                        ? 'app.patrol.integrationRequest.connect.copied'
                        : 'app.patrol.integrationRequest.connect.copy'
                      ) | translate
                    "
                    [attr.title]="
                      (isCopied('form-uri:' + i)
                        ? 'app.patrol.integrationRequest.connect.copied'
                        : 'app.patrol.integrationRequest.connect.copy'
                      ) | translate
                    "
                    (click)="copyText(uri, 'form-uri:' + i)"
                  >
                    <img
                      class="copy-icon"
                      [src]="
                        isCopied('form-uri:' + i)
                          ? '/public/svgs/copy-check.svg'
                          : '/public/svgs/copy.svg'
                      "
                      width="16"
                      height="16"
                      alt=""
                      aria-hidden="true"
                    />
                  </button>
                  <button
                    type="button"
                    class="uri-editor__remove"
                    [disabled]="formState.redirectUris.length <= 1"
                    (click)="removeRedirectUri(i)"
                    [attr.aria-label]="'app.patrol.integrationRequest.fields.removeUri' | translate"
                  >
                    ×
                  </button>
                </div>
              }
              <button type="button" class="uri-editor__add" (click)="addRedirectUri()">
                {{ 'app.patrol.integrationRequest.fields.addUri' | translate }}
              </button>
            </div>
            <small>{{ 'app.patrol.integrationRequest.fields.redirectUrisHint' | translate }}</small>
          </label>

          <label class="field">
            <span>{{ 'app.patrol.integrationRequest.fields.webhookUrl' | translate }}</span>
            <input
              type="url"
              name="webhookUrl"
              [(ngModel)]="formState.webhookUrl"
              autocomplete="off"
              placeholder="https://"
            />
            <small>{{ 'app.patrol.integrationRequest.fields.webhookUrlHint' | translate }}</small>
          </label>

          <fieldset class="scopes-fieldset">
            <legend>{{ 'app.patrol.integrationRequest.fields.scopes' | translate }}</legend>
            <ul class="scope-list">
              @for (scope of scopeItems(); track scope.scopeId) {
                <li>
                  <label class="scope-option">
                    <input
                      type="checkbox"
                      [checked]="isScopeSelected(scope.scopeId ?? '')"
                      (change)="toggleScope(scope.scopeId ?? '', $any($event.target).checked)"
                    />
                    <span>{{ scopeLabel(scope.scopeId ?? '') }}</span>
                  </label>
                </li>
              }
            </ul>
          </fieldset>

          <div class="form-actions">
            <button type="button" class="form-actions__cancel" (click)="cancelForm()">
              {{ 'app.patrol.integrationRequest.cancel' | translate }}
            </button>
            <app-cta-button size="small" type="submit" [disabled]="saving()">
              <span cta-label>
                @if (saving()) {
                  {{ 'app.patrol.integrationRequest.saving' | translate }}
                } @else {
                  {{ 'app.patrol.integrationRequest.save' | translate }}
                }
              </span>
            </app-cta-button>
          </div>
        </form>
      } @else {
        <header class="requests-section__header">
          <div class="requests-section__intro">
            <div class="requests-section__icon" aria-hidden="true">
              <img src="/public/svgs/identity-id.svg" alt="" width="24" height="24" />
            </div>
            <div class="requests-section__copy">
              <div class="requests-section__title-row">
                <h2 id="integration-requests-heading" class="requests-section__title">
                  {{ 'app.patrol.integrationRequest.requestsTitle' | translate }}
                </h2>
                @if (requestCount() > 0) {
                  <span class="requests-section__count">
                    {{ requestCountLabel() }}
                  </span>
                }
              </div>
              <p class="requests-section__subtitle">
                {{ 'app.patrol.integrationRequest.requestsSubtitle' | translate }}
              </p>
            </div>
          </div>
        </header>

        @if (requestCount() === 0) {
          <div
            class="requests-empty"
            [attr.aria-label]="'app.patrol.integrationRequest.emptyAria' | translate"
          >
            <div class="requests-empty__hero">
              <div class="requests-empty__icon" aria-hidden="true">
                <img src="/public/svgs/globe.svg" alt="" width="28" height="28" />
              </div>
              <h3 class="requests-empty__title">
                {{ 'app.patrol.integrationRequest.emptyTitle' | translate }}
              </h3>
              <p class="requests-empty__hint">
                {{ 'app.patrol.integrationRequest.emptyHint' | translate }}
              </p>
              <app-cta-button size="small" (clicked)="openCreateForm()">
                <span cta-label>{{ 'app.patrol.integrationRequest.newRequest' | translate }}</span>
              </app-cta-button>
            </div>

            <div class="requests-onboarding">
              <h4 class="requests-onboarding__title">
                {{ 'app.patrol.integrationRequest.onboardingTitle' | translate }}
              </h4>
              <ol class="requests-onboarding__steps">
                @for (step of onboardingSteps; track step.titleKey) {
                  <li class="requests-onboarding__step">
                    <span class="requests-onboarding__step-index" aria-hidden="true">
                      {{ $index + 1 }}
                    </span>
                    <div>
                      <p class="requests-onboarding__step-title">
                        {{ step.titleKey | translate }}
                      </p>
                      <p class="requests-onboarding__step-hint">
                        {{ step.hintKey | translate }}
                      </p>
                    </div>
                  </li>
                }
              </ol>
            </div>
          </div>
        } @else {
          <div class="requests-section__toolbar">
            <app-cta-button size="small" (clicked)="openCreateForm()">
              <span cta-label>{{ 'app.patrol.integrationRequest.newRequest' | translate }}</span>
            </app-cta-button>
          </div>

          @if (statusSummary().length > 0) {
            <ul class="requests-summary" aria-label="Request status summary">
              @for (item of statusSummary(); track item.status) {
                <li class="requests-summary__chip" [attr.data-status]="item.status">
                  <span class="requests-summary__count">{{ item.count }}</span>
                  <span class="requests-summary__label">{{ statusLabel(item.status) }}</span>
                </li>
              }
            </ul>
          }

          <ul class="request-list">
            @for (request of requests(); track request.id) {
              <li class="request-card">
                <div class="request-card__main">
                  <header class="request-card__header">
                    <div class="request-card__identity">
                      <div
                        class="request-card__avatar"
                        [attr.data-provider]="request.providerType ?? 'custom'"
                        aria-hidden="true"
                      >
                        {{ appInitial(request.appName) }}
                      </div>
                      <div>
                        <p class="request-card__name">{{ request.appName }}</p>
                        <p class="request-card__client-id">
                          <span class="request-card__label">
                            {{ 'app.patrol.integrationRequest.clientIdLabel' | translate }}:
                          </span>
                          <code>{{ request.requestedClientId }}</code>
                        </p>
                      </div>
                    </div>
                    <div class="request-card__badges">
                      <span class="request-card__badge request-card__badge--status" [attr.data-status]="request.status">
                        {{ statusLabel(request.status) }}
                      </span>
                      <span
                        class="request-card__badge request-card__badge--environment"
                        [attr.data-environment]="request.environment ?? 'sandbox'"
                      >
                        {{ environmentLabel(request.environment) }}
                      </span>
                    </div>
                  </header>

                  <dl class="request-card__details">
                    <div class="request-card__detail">
                      <dt>{{ 'app.patrol.integrationRequest.platformLabel' | translate }}</dt>
                      <dd>{{ providerLabel(request.providerType) }}</dd>
                    </div>
                    @if (formatTimestamp(request.submittedAt); as submittedAt) {
                      <div class="request-card__detail">
                        <dt>{{ 'app.patrol.integrationRequest.submittedAt' | translate }}</dt>
                        <dd>{{ submittedAt | date: 'mediumDate' }}</dd>
                      </div>
                    } @else {
                      @if (formatTimestamp(request.updatedAt); as updatedAt) {
                        <div class="request-card__detail">
                          <dt>{{ 'app.patrol.integrationRequest.updatedAt' | translate }}</dt>
                          <dd>{{ updatedAt | date: 'mediumDate' }}</dd>
                        </div>
                      }
                    }
                  </dl>

                  @if ((request.scopeIds ?? []).length > 0) {
                    <div class="request-card__scopes">
                      <span class="request-card__label">
                        {{ 'app.patrol.integrationRequest.scopesLabel' | translate }}
                      </span>
                      <ul class="request-card__scope-list">
                        @for (scopeId of scopePreview(request).visible; track scopeId) {
                          <li class="request-card__scope-chip">{{ scopeLabel(scopeId) }}</li>
                        }
                        @if (scopePreview(request).overflow > 0) {
                          <li class="request-card__scope-chip request-card__scope-chip--more">
                            {{
                              'app.patrol.integrationRequest.scopesMore'
                                | translate: { count: scopePreview(request).overflow }
                            }}
                          </li>
                        }
                      </ul>
                    </div>
                  }

                  @if (request.reviewerNotes) {
                    <div class="request-card__notes">
                      <span class="request-card__label">
                        {{ 'app.patrol.integrationRequest.adminNotes' | translate }}
                      </span>
                      <p>{{ request.reviewerNotes }}</p>
                    </div>
                  }

                  @if (isApproved(request)) {
                    <details
                      class="connect-guide"
                      [attr.aria-label]="
                        'app.patrol.integrationRequest.connect.aria' | translate
                      "
                    >
                      <summary class="connect-guide__summary">
                        <div class="connect-guide__summary-main">
                          <span class="connect-guide__title">
                            {{ 'app.patrol.integrationRequest.connect.title' | translate }}
                          </span>
                          <span class="connect-guide__summary-meta">
                            <code>{{ registeredClientId(request) }}</code>
                            <span class="connect-guide__auth-note">
                              {{
                                'app.patrol.integrationRequest.connect.pkceNote'
                                  | translate
                              }}
                            </span>
                          </span>
                          <span class="connect-guide__expand-hint">
                            {{
                              'app.patrol.integrationRequest.connect.expandHint'
                                | translate
                            }}
                          </span>
                        </div>
                      </summary>

                      <div class="connect-guide__body">
                        <p class="connect-guide__subtitle">
                          {{
                            'app.patrol.integrationRequest.connect.subtitle' | translate
                          }}
                        </p>

                        <div class="connect-guide__credentials">
                          <h4 class="connect-guide__section-title">
                            {{
                              'app.patrol.integrationRequest.connect.credentialsTitle'
                                | translate
                            }}
                          </h4>

                          <div class="connect-guide__field">
                            <span class="connect-guide__field-label">
                              {{
                                'app.patrol.integrationRequest.connect.clientId'
                                  | translate
                              }}
                            </span>
                            <div class="connect-guide__value-row">
                              <code>{{ registeredClientId(request) }}</code>
                              <button
                                type="button"
                                class="connect-guide__copy"
                                [attr.aria-label]="
                                  (isCopied(
                                    'client:' +
                                      (request.id ?? registeredClientId(request))
                                  )
                                    ? 'app.patrol.integrationRequest.connect.copied'
                                    : 'app.patrol.integrationRequest.connect.copy'
                                  ) | translate
                                "
                                [attr.title]="
                                  (isCopied(
                                    'client:' +
                                      (request.id ?? registeredClientId(request))
                                  )
                                    ? 'app.patrol.integrationRequest.connect.copied'
                                    : 'app.patrol.integrationRequest.connect.copy'
                                  ) | translate
                                "
                                (click)="
                                  copyText(
                                    registeredClientId(request),
                                    'client:' +
                                      (request.id ?? registeredClientId(request))
                                  )
                                "
                              >
                                <img
                                  class="copy-icon"
                                  [src]="
                                    isCopied(
                                      'client:' +
                                        (request.id ?? registeredClientId(request))
                                    )
                                      ? '/public/svgs/copy-check.svg'
                                      : '/public/svgs/copy.svg'
                                  "
                                  width="16"
                                  height="16"
                                  alt=""
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                          </div>

                          @if ((request.redirectUris ?? []).length > 0) {
                            <div class="connect-guide__field">
                              <span class="connect-guide__field-label">
                                {{
                                  'app.patrol.integrationRequest.connect.redirectUris'
                                    | translate
                                }}
                              </span>
                              <ul class="connect-guide__uri-list">
                                @for (uri of request.redirectUris; track uri) {
                                  <li>
                                    <div class="connect-guide__value-row">
                                      <code>{{ uri }}</code>
                                      <button
                                        type="button"
                                        class="connect-guide__copy"
                                        [attr.aria-label]="
                                          (isCopied(
                                            'redirect:' +
                                              (request.id ?? '') +
                                              ':' +
                                              uri
                                          )
                                            ? 'app.patrol.integrationRequest.connect.copied'
                                            : 'app.patrol.integrationRequest.connect.copy'
                                          ) | translate
                                        "
                                        [attr.title]="
                                          (isCopied(
                                            'redirect:' +
                                              (request.id ?? '') +
                                              ':' +
                                              uri
                                          )
                                            ? 'app.patrol.integrationRequest.connect.copied'
                                            : 'app.patrol.integrationRequest.connect.copy'
                                          ) | translate
                                        "
                                        (click)="
                                          copyText(
                                            uri,
                                            'redirect:' +
                                              (request.id ?? '') +
                                              ':' +
                                              uri
                                          )
                                        "
                                      >
                                        <img
                                          class="copy-icon"
                                          [src]="
                                            isCopied(
                                              'redirect:' +
                                                (request.id ?? '') +
                                                ':' +
                                                uri
                                            )
                                              ? '/public/svgs/copy-check.svg'
                                              : '/public/svgs/copy.svg'
                                          "
                                          width="16"
                                          height="16"
                                          alt=""
                                          aria-hidden="true"
                                        />
                                      </button>
                                    </div>
                                  </li>
                                }
                              </ul>
                            </div>
                          }

                          @if ((request.scopeIds ?? []).length > 0) {
                            <div class="connect-guide__field">
                              <span class="connect-guide__field-label">
                                {{
                                  'app.patrol.integrationRequest.connect.scopes'
                                    | translate
                                }}
                              </span>
                              <ul class="connect-guide__scope-list">
                                @for (scopeId of request.scopeIds; track scopeId) {
                                  <li>
                                    <code>{{ scopeId }}</code>
                                    <span class="connect-guide__scope-label">
                                      {{ scopeLabel(scopeId) }}
                                    </span>
                                  </li>
                                }
                              </ul>
                            </div>
                          }

                          <div class="connect-guide__field connect-guide__webhook">
                            <span class="connect-guide__field-label">
                              {{
                                'app.patrol.integrationRequest.webhook.title'
                                  | translate
                              }}
                            </span>
                            <p class="connect-guide__webhook-hint">
                              {{
                                'app.patrol.integrationRequest.webhook.hint'
                                  | translate
                              }}
                            </p>
                            @if (request.webhookConfigured) {
                              <p class="connect-guide__webhook-status" data-ready="true">
                                {{
                                  'app.patrol.integrationRequest.webhook.configured'
                                    | translate
                                }}
                              </p>
                            } @else {
                              <p class="connect-guide__webhook-status">
                                {{
                                  'app.patrol.integrationRequest.webhook.notConfigured'
                                    | translate
                                }}
                              </p>
                            }
                            <div class="connect-guide__webhook-url">
                              <span>
                                {{
                                  'app.patrol.integrationRequest.fields.webhookUrl'
                                    | translate
                                }}
                              </span>
                              <div class="connect-guide__value-row">
                                <input
                                  type="url"
                                  class="connect-guide__webhook-input"
                                  [name]="'webhookUrl-' + (request.id ?? '')"
                                  [ngModel]="webhookDraft(request)"
                                  (ngModelChange)="
                                    setWebhookDraft(request.id ?? '', $event)
                                  "
                                  [ngModelOptions]="{ standalone: true }"
                                  autocomplete="off"
                                  placeholder="https://"
                                />
                                <button
                                  type="button"
                                  class="connect-guide__webhook-save"
                                  [disabled]="
                                    webhookSavingId() === request.id ||
                                    !webhookDraft(request).trim()
                                  "
                                  (click)="saveWebhook(request)"
                                >
                                  @if (webhookSavingId() === request.id) {
                                    {{
                                      'app.patrol.integrationRequest.webhook.saving'
                                        | translate
                                    }}
                                  } @else if (request.webhookConfigured) {
                                    {{
                                      'app.patrol.integrationRequest.webhook.rotate'
                                        | translate
                                    }}
                                  } @else {
                                    {{
                                      'app.patrol.integrationRequest.webhook.generate'
                                        | translate
                                    }}
                                  }
                                </button>
                              </div>
                            </div>
                            @if (revealedWebhookSecret(request.id); as secret) {
                              <div
                                class="connect-guide__webhook-secret"
                                role="status"
                              >
                                <p class="connect-guide__webhook-secret-warning">
                                  {{
                                    'app.patrol.integrationRequest.webhook.secretOnce'
                                      | translate
                                  }}
                                </p>
                                <div class="connect-guide__value-row">
                                  <code>{{ secret }}</code>
                                  <button
                                    type="button"
                                    class="connect-guide__copy"
                                    [attr.aria-label]="
                                      (isCopied(
                                        'webhook-secret:' + (request.id ?? '')
                                      )
                                        ? 'app.patrol.integrationRequest.connect.copied'
                                        : 'app.patrol.integrationRequest.connect.copy'
                                      ) | translate
                                    "
                                    [attr.title]="
                                      (isCopied(
                                        'webhook-secret:' + (request.id ?? '')
                                      )
                                        ? 'app.patrol.integrationRequest.connect.copied'
                                        : 'app.patrol.integrationRequest.connect.copy'
                                      ) | translate
                                    "
                                    (click)="
                                      copyText(
                                        secret,
                                        'webhook-secret:' + (request.id ?? '')
                                      )
                                    "
                                  >
                                    <img
                                      class="copy-icon"
                                      [src]="
                                        isCopied(
                                          'webhook-secret:' + (request.id ?? '')
                                        )
                                          ? '/public/svgs/copy-check.svg'
                                          : '/public/svgs/copy.svg'
                                      "
                                      width="16"
                                      height="16"
                                      alt=""
                                      aria-hidden="true"
                                    />
                                  </button>
                                </div>
                              </div>
                            }
                            @if (request.webhookConfigured) {
                              <div class="connect-guide__webhook-history">
                                <div class="connect-guide__webhook-history-head">
                                  <span class="connect-guide__field-label">
                                    {{
                                      'app.patrol.integrationRequest.webhook.historyTitle'
                                        | translate
                                    }}
                                  </span>
                                  <button
                                    type="button"
                                    class="connect-guide__webhook-refresh"
                                    [disabled]="
                                      webhookDeliveriesLoadingId() === request.id
                                    "
                                    (click)="loadWebhookDeliveries(request)"
                                  >
                                    @if (
                                      webhookDeliveriesLoadingId() === request.id
                                    ) {
                                      {{
                                        'app.patrol.integrationRequest.webhook.historyLoading'
                                          | translate
                                      }}
                                    } @else {
                                      {{
                                        'app.patrol.integrationRequest.webhook.historyRefresh'
                                          | translate
                                      }}
                                    }
                                  </button>
                                </div>
                                @if (deliveriesFor(request).length === 0) {
                                  <p class="connect-guide__webhook-hint">
                                    {{
                                      'app.patrol.integrationRequest.webhook.historyEmpty'
                                        | translate
                                    }}
                                  </p>
                                } @else {
                                  <ul class="connect-guide__delivery-list">
                                    @for (
                                      delivery of deliveriesFor(request);
                                      track delivery.id
                                    ) {
                                      <li
                                        class="connect-guide__delivery"
                                        [attr.data-delivered]="
                                          delivery.delivered ? 'true' : 'false'
                                        "
                                      >
                                        <div class="connect-guide__delivery-main">
                                          <span
                                            class="connect-guide__delivery-status"
                                          >
                                            @if (delivery.delivered) {
                                              {{
                                                'app.patrol.integrationRequest.webhook.deliveryOk'
                                                  | translate
                                              }}
                                            } @else {
                                              {{
                                                'app.patrol.integrationRequest.webhook.deliveryFailed'
                                                  | translate
                                              }}
                                            }
                                          </span>
                                          <code>{{ delivery.eventType }}</code>
                                          @if (delivery.httpStatus) {
                                            <span
                                              class="connect-guide__delivery-meta"
                                            >
                                              HTTP {{ delivery.httpStatus }}
                                            </span>
                                          }
                                          @if (delivery.createdAt) {
                                            <span
                                              class="connect-guide__delivery-meta"
                                            >
                                              {{
                                                delivery.createdAt * 1000
                                                  | date: 'short'
                                              }}
                                            </span>
                                          }
                                        </div>
                                        @if (delivery.errorMessage) {
                                          <p
                                            class="connect-guide__delivery-error"
                                          >
                                            {{ delivery.errorMessage }}
                                          </p>
                                        }
                                        <button
                                          type="button"
                                          class="connect-guide__webhook-resend"
                                          [disabled]="
                                            !request.webhookConfigured ||
                                            webhookResendingId() === delivery.id
                                          "
                                          (click)="
                                            resendWebhookDelivery(
                                              request,
                                              delivery
                                            )
                                          "
                                        >
                                          @if (
                                            webhookResendingId() === delivery.id
                                          ) {
                                            {{
                                              'app.patrol.integrationRequest.webhook.resending'
                                                | translate
                                            }}
                                          } @else {
                                            {{
                                              'app.patrol.integrationRequest.webhook.resend'
                                                | translate
                                            }}
                                          }
                                        </button>
                                      </li>
                                    }
                                  </ul>
                                }
                              </div>
                            }
                          </div>
                        </div>

                        <div class="connect-guide__steps-block">
                          <h4 class="connect-guide__section-title">
                            {{
                              'app.patrol.integrationRequest.connect.stepsTitle'
                                | translate
                            }}
                          </h4>
                          <ol class="connect-guide__steps">
                            @for (step of connectSteps; track step.titleKey) {
                              <li class="connect-guide__step">
                                <span
                                  class="connect-guide__step-index"
                                  aria-hidden="true"
                                >
                                  {{ $index + 1 }}
                                </span>
                                <div>
                                  <p class="connect-guide__step-title">
                                    {{ step.titleKey | translate }}
                                  </p>
                                  <p class="connect-guide__step-hint">
                                    {{ step.hintKey | translate }}
                                  </p>
                                </div>
                              </li>
                            }
                          </ol>
                        </div>

                        <div class="connect-guide__endpoints">
                          <h4 class="connect-guide__section-title">
                            {{
                              'app.patrol.integrationRequest.connect.endpointsTitle'
                                | translate
                            }}
                          </h4>
                          <ul class="connect-guide__endpoint-list">
                            @for (endpoint of oauthEndpoints; track endpoint.id) {
                              <li>
                                <span class="connect-guide__endpoint-label">
                                  {{ endpoint.labelKey | translate }}
                                </span>
                                <div class="connect-guide__value-row">
                                  <code>{{ endpoint.url }}</code>
                                  <button
                                    type="button"
                                    class="connect-guide__copy"
                                    [attr.aria-label]="
                                      (isCopied(
                                        'endpoint:' +
                                          (request.id ?? '') +
                                          ':' +
                                          endpoint.id
                                      )
                                        ? 'app.patrol.integrationRequest.connect.copied'
                                        : 'app.patrol.integrationRequest.connect.copy'
                                      ) | translate
                                    "
                                    [attr.title]="
                                      (isCopied(
                                        'endpoint:' +
                                          (request.id ?? '') +
                                          ':' +
                                          endpoint.id
                                      )
                                        ? 'app.patrol.integrationRequest.connect.copied'
                                        : 'app.patrol.integrationRequest.connect.copy'
                                      ) | translate
                                    "
                                    (click)="
                                      copyText(
                                        endpoint.url,
                                        'endpoint:' +
                                          (request.id ?? '') +
                                          ':' +
                                          endpoint.id
                                      )
                                    "
                                  >
                                    <img
                                      class="copy-icon"
                                      [src]="
                                        isCopied(
                                          'endpoint:' +
                                            (request.id ?? '') +
                                            ':' +
                                            endpoint.id
                                        )
                                          ? '/public/svgs/copy-check.svg'
                                          : '/public/svgs/copy.svg'
                                      "
                                      width="16"
                                      height="16"
                                      alt=""
                                      aria-hidden="true"
                                    />
                                  </button>
                                </div>
                              </li>
                            }
                          </ul>
                        </div>

                        @if (sampleAuthorizeUrl(request); as authorizeSample) {
                          <div class="connect-guide__sample">
                            <h4 class="connect-guide__section-title">
                              {{
                                'app.patrol.integrationRequest.connect.sampleTitle'
                                  | translate
                              }}
                            </h4>
                            <p class="connect-guide__sample-hint">
                              {{
                                'app.patrol.integrationRequest.connect.sampleHint'
                                  | translate
                              }}
                            </p>
                            <div
                              class="connect-guide__value-row connect-guide__value-row--stack"
                            >
                              <code class="connect-guide__sample-url">{{
                                authorizeSample
                              }}</code>
                              <button
                                type="button"
                                class="connect-guide__copy"
                                [attr.aria-label]="
                                  (isCopied(
                                    'sample:' +
                                      (request.id ?? registeredClientId(request))
                                  )
                                    ? 'app.patrol.integrationRequest.connect.copied'
                                    : 'app.patrol.integrationRequest.connect.copySample'
                                  ) | translate
                                "
                                [attr.title]="
                                  (isCopied(
                                    'sample:' +
                                      (request.id ?? registeredClientId(request))
                                  )
                                    ? 'app.patrol.integrationRequest.connect.copied'
                                    : 'app.patrol.integrationRequest.connect.copySample'
                                  ) | translate
                                "
                                (click)="
                                  copyText(
                                    authorizeSample,
                                    'sample:' +
                                      (request.id ?? registeredClientId(request))
                                  )
                                "
                              >
                                <img
                                  class="copy-icon"
                                  [src]="
                                    isCopied(
                                      'sample:' +
                                        (request.id ?? registeredClientId(request))
                                    )
                                      ? '/public/svgs/copy-check.svg'
                                      : '/public/svgs/copy.svg'
                                  "
                                  width="16"
                                  height="16"
                                  alt=""
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                          </div>
                        }

                        <p class="connect-guide__userinfo-note">
                          {{
                            'app.patrol.integrationRequest.connect.userinfoNote'
                              | translate
                          }}
                        </p>
                      </div>
                    </details>
                  }
                </div>

                @if (isEditable(request) || isSubmittable(request)) {
                  <div class="request-card__actions">
                    @if (isEditable(request)) {
                      <app-cta-button size="small" variant="secondary" (clicked)="openEditForm(request)">
                        <span cta-label>{{ 'app.patrol.integrationRequest.edit' | translate }}</span>
                      </app-cta-button>
                    }
                    @if (isSubmittable(request)) {
                      <app-cta-button
                        size="small"
                        [disabled]="isActing(request.id)"
                        (clicked)="submitRequest(request)"
                      >
                        <span cta-label>
                          @if (isActing(request.id)) {
                            {{ 'app.patrol.integrationRequest.submitting' | translate }}
                          } @else {
                            {{ 'app.patrol.integrationRequest.submit' | translate }}
                          }
                        </span>
                      </app-cta-button>
                    }
                  </div>
                }
              </li>
            }
          </ul>
        }
      }
    </section>
  }
</div>
